import React from 'react';
import { Link } from 'react-router-dom';


const APP_NAME = process.env.REACT_APP_APPLICATION_NAME || 'LMS';
const MOBILE_APP_NAME = `${APP_NAME} Mobile`;
const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME || APP_NAME;

function resolveSiteOrigin() {
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}

const SITE_URL = resolveSiteOrigin();
const CONTACT_EMAIL =
  process.env.REACT_APP_PRIVACY_CONTACT_EMAIL?.trim() ||
  process.env.REACT_APP_COMPANY_EMAIL?.trim() ||
  'reachus@ellipsonic.com';

const LAST_UPDATED = 'July 5, 2026';
const PRIVACY_POLICY_PATH = '/privacy';
const DELETE_ACCOUNT_PATH = '/delete-account';

function PrivacyPolicy() {
  return (
    <div className="tw-min-h-[100vh] tw-bg-[#ffffff] tw-text-[#171717] [font-family:-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] tw-leading-[1.6] privacy-policy-page">
      <header className="tw-border-b-[1px] tw-border-solid tw-border-b-[#e5e5e5] tw-bg-[#ffffff] tw-sticky tw-top-0 tw-z-[10] privacy-policy-header">
        <div className="tw-max-w-[800px] tw-m-[0_auto] tw-p-[16px_24px] tw-flex tw-items-center tw-justify-between tw-gap-[16px] tw-flex-wrap privacy-policy-header-inner">
          <span className="tw-text-[13px] tw-text-[#737373] privacy-policy-updated">Last updated: {LAST_UPDATED}</span>
        </div>
      </header>

      <main className={"tw-max-w-[800px] tw-m-[0_auto] tw-p-[40px_24px_64px] [&_h1]:tw-text-[32px] [&_h1]:tw-font-bold [&_h1]:tw-mb-[16px] [&_h1]:tw-text-[#171717] [&_section]:tw-mb-[32px] [&_h2]:tw-text-[20px] [&_h2]:tw-font-semibold [&_h2]:tw-mb-[12px] [&_h2]:tw-text-[#171717] [&_h3]:tw-text-[16px] [&_h3]:tw-font-semibold [&_h3]:tw-m-[16px_0_8px] [&_h3]:tw-text-[#262626] [&_p]:tw-text-[15px] [&_p]:tw-text-[#404040] [&_p]:tw-mb-[12px] [&_ul]:tw-m-[0_0_12px_20px] [&_ul]:tw-p-0 [&_li]:tw-text-[15px] [&_li]:tw-text-[#404040] [&_li]:tw-mb-[8px] [&_a]:tw-text-[#0058ff] [&_a]:tw-no-underline [&_a:hover]:tw-underline max-[640px]:tw-p-[24px_16px_48px] max-[640px]:[&_h1]:tw-text-[26px] privacy-policy-main"}>
        <h1>Privacy Policy</h1>
        <p className="tw-text-[16px] tw-text-[#404040] tw-mb-[32px] privacy-policy-intro">
          This Privacy Policy describes how {COMPANY_NAME} (&quot;we,&quot; &quot;us,&quot; or
          &quot;our&quot;) collects, uses, discloses, and protects personal information when you use
          the {APP_NAME} learning platform, including our website and the {MOBILE_APP_NAME} app
          (together, the &quot;Service&quot;). This policy applies to trainees and other users who
          access the Service through your organization&apos;s academy.
        </p>
        <p className="tw-text-[16px] tw-text-[#404040] tw-mb-[32px] privacy-policy-intro">
          By using the Service, you agree to this Privacy Policy. If you do not agree, please do not
          use the Service.
        </p>

        <section id="information-we-collect">
          <h2>1. Information We Collect</h2>
          <p>
            We collect information you provide directly, information generated through your use of
            the Service, and limited technical information from your device.
          </p>

          <h3>Account and profile information</h3>
          <p>
            When you register or sign in, we may collect your name, email address, phone number,
            password (stored in hashed form only), organization affiliation, user role, profile
            photo, timezone, and language preferences.
          </p>

          <h3>Learning and activity data</h3>
          <p>
            We collect data related to your learning activity, including course enrollments,
            video watch progress, document views, assessment and quiz responses, survey
            responses, classroom participation, assignment submissions, certificates earned, notes,
            comments, ratings, gamification points, badges, leaderboard data, tasks, and support
            tickets.
          </p>

          <h3>Content you upload</h3>
          <p>
            When you use features such as the social feed, profile photo upload, or assignment
            submissions, we collect photos, videos, documents, and other files you choose to upload.
          </p>

          <h3>Device and technical information</h3>
          <p>
            We may collect device type, operating system, app or browser version, push notification
            tokens, IP address, general usage logs, and diagnostic information needed to operate,
            secure, troubleshoot, and improve the Service. We do not use your data for third-party
            advertising or sell your personal information.
          </p>

          <h3>Cookies and local storage (website)</h3>
          <p>
            Our website uses browser local storage and similar technologies to keep you signed in,
            remember preferences, and support core functionality. We do not use cookies for
            cross-site advertising.
          </p>
        </section>

        <section id="mobile-permissions">
          <h2>2. Mobile App Permissions</h2>
          <p>
            The {MOBILE_APP_NAME} app may request the following device permissions. We access these
            only when you use the related feature, and you can revoke them in your device settings
            at any time.
          </p>
          <ul>
            <li>
              <strong>Camera and photo library</strong> — to capture or select photos and videos for
              feed posts, profile pictures, and assignment submissions.
            </li>
            <li>
              <strong>Microphone</strong> — when recording or sharing videos that include audio.
            </li>
            <li>
              <strong>Notifications</strong> — to deliver course updates, classroom reminders, and
              other Service-related alerts via push notifications.
            </li>
            <li>
              <strong>Secure storage</strong> — to store authentication tokens locally on your
              device so you remain signed in.
            </li>
          </ul>
        </section>

        <section id="how-we-use">
          <h2>3. How We Use Your Information</h2>
          <p>We use collected information to:</p>
          <ul>
            <li>Provide, maintain, personalize, and improve the learning experience</li>
            <li>Authenticate users and manage accounts across organizations</li>
            <li>Track progress, issue certificates, and display analytics to you and your organization</li>
            <li>Send transactional emails, in-app notifications, and push notifications</li>
            <li>Facilitate live classes, classroom communication, and community features</li>
            <li>Respond to support requests and investigate technical issues</li>
            <li>Detect, prevent, and address fraud, abuse, or security incidents</li>
            <li>Comply with legal obligations and enforce our terms</li>
          </ul>
        </section>

        <section id="how-we-share">
          <h2>4. How We Share Information</h2>
          <p>
            <strong>We do not sell your personal information.</strong> We may share information in
            the following circumstances:
          </p>
          <ul>
            <li>
              <strong>Your organization</strong> — administrators and trainers within your academy
              may access learner progress, submissions, and activity as part of the Service.
            </li>
            <li>
              <strong>Service providers</strong> — trusted vendors that help us operate the Service,
              subject to contractual confidentiality and data-protection obligations. These may
              include:
              <ul>
                <li>Cloud hosting and file storage (e.g., Amazon Web Services / S3)</li>
                <li>Email delivery services</li>
                <li>Push notification services (e.g., Firebase Cloud Messaging)</li>
                <li>Video streaming and media delivery</li>
                <li>Live-class providers (e.g., Zoom)</li>
                <li>Chat and community services (e.g., GetStream)</li>
                <li>SMS providers, when enabled by your organization (e.g., Plivo)</li>
              </ul>
            </li>
            <li>
              <strong>Legal requirements</strong> — when required by law, regulation, court order, or
              to protect the rights, safety, and security of users and the Service.
            </li>
            <li>
              <strong>Business transfers</strong> — in connection with a merger, acquisition, or
              sale of assets, subject to continued protection of your information.
            </li>
          </ul>
          <p>
            Third-party services have their own privacy policies. We encourage you to review them:
          </p>
          <ul>
            <li>
              <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">
                Google Firebase Privacy
              </a>
            </li>
            <li>
              <a href="https://aws.amazon.com/privacy/" target="_blank" rel="noopener noreferrer">
                AWS Privacy
              </a>
            </li>
            <li>
              <a href="https://getstream.io/privacy/" target="_blank" rel="noopener noreferrer">
                GetStream Privacy
              </a>
            </li>
            <li>
              <a href="https://explore.zoom.us/en/privacy/" target="_blank" rel="noopener noreferrer">
                Zoom Privacy
              </a>
            </li>
          </ul>
        </section>

        <section id="data-retention">
          <h2>5. Data Retention and Deletion</h2>
          <p>
            We retain your information for as long as your account is active or as needed to provide
            the Service, comply with legal obligations, resolve disputes, and enforce our agreements.
            Backup copies may persist for a limited period after deletion.
          </p>
        </section>

        <section id="account-deletion">
          <h2>6. Account Deletion</h2>
          <p>
            You may request deletion of your account and associated personal data at any time:
          </p>
          <ul>
            <li>
              <strong>Without signing in:</strong> use our{' '}
              <Link to={DELETE_ACCOUNT_PATH}>Delete Account page</Link> — enter your email, verify
              with a one-time code, and confirm deletion.
            </li>
            <li>
              <strong>In the mobile app:</strong> open <strong>Settings → Delete Account</strong>,
              follow the verification steps, and confirm deletion.
            </li>
            <li>
              <strong>On the website (signed in):</strong> open <strong>Settings → Delete Account</strong>
              {CONTACT_EMAIL ? (
                <>
                  , or email us at{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> from your registered email
                  address
                </>
              ) : null}
              .
            </li>
          </ul>
          <p>
            After verified deletion, we will delete or anonymize your personal data in accordance
            with applicable law and our retention policies, except where we must retain certain
            records for legal or security purposes.
          </p>
          <p>
            For Google Play users: use{' '}
            <Link to={DELETE_ACCOUNT_PATH}>{SITE_URL ? `${SITE_URL}${DELETE_ACCOUNT_PATH}` : DELETE_ACCOUNT_PATH}</Link>{' '}
            to request account deletion without logging in.
          </p>
        </section>

        <section id="security">
          <h2>7. Security</h2>
          <p>
            We use industry-standard safeguards, including encrypted connections (HTTPS/TLS), secure
            authentication tokens, access controls, and hashed passwords. No method of transmission
            or storage is completely secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <section id="your-rights">
          <h2>8. Your Rights and Choices</h2>
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li>Access, update, or correct your profile information in the app or website</li>
            <li>Request a copy of personal data we hold about you</li>
            <li>Request deletion of your account and personal data (see Section 6)</li>
            <li>Object to or restrict certain processing, where applicable by law</li>
            <li>Withdraw consent for optional features such as push notifications</li>
            <li>Opt out of non-essential communications through notification settings</li>
          </ul>

          <h3>European Economic Area (GDPR)</h3>
          <p>
            If you are in the EEA, we process personal data based on contract performance (providing
            the Service), legitimate interests (security and improvement), legal obligations, and your
            consent where required. You may lodge a complaint with your local data protection
            authority.
          </p>

          <h3>California (CCPA/CPRA)</h3>
          <p>
            California residents have the right to know what personal information we collect, request
            deletion, and correct inaccurate information. We do not sell or share personal
            information for cross-context behavioral advertising.
          </p>

          <p>
            To exercise your rights, use the in-app settings described above
            {CONTACT_EMAIL ? (
              <>
                {' '}
                or contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </>
            ) : null}
            . We may verify your identity before fulfilling requests.
          </p>
        </section>

        <section id="children">
          <h2>9. Children&apos;s Privacy</h2>
          <p>
            The Service is intended for users enrolled through their organization or with parental
            or institutional consent where required. We do not knowingly collect personal information
            from children under 13 (or the applicable age in your jurisdiction) without appropriate
            authorization. If you believe we have collected such information, please contact us
            promptly.
          </p>
        </section>

        <section id="international">
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be processed and stored in countries other than your own, including
            where our service providers operate. We take steps to ensure appropriate safeguards are in
            place for international transfers as required by applicable law.
          </p>
        </section>

        <section id="changes">
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will post the revised policy on
            this page (<Link to={PRIVACY_POLICY_PATH}>Privacy Policy</Link>) and update the
            &quot;Last updated&quot; date. Material changes may also be communicated through the
            Service. Continued use after changes become effective constitutes acceptance of the
            updated policy.
          </p>
        </section>

        <section id="contact">
          <h2>12. Contact Us</h2>
          <p>
            For privacy questions, data requests, or complaints, contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>
      </main>

      <footer className={"tw-border-t-[1px] tw-border-solid tw-border-t-[#e5e5e5] tw-p-[24px] tw-text-center [&_p]:tw-m-0 [&_p]:tw-text-[13px] [&_p]:tw-text-[#737373] privacy-policy-footer"}>
        <p>
          &copy; {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
